/**
 * Notes (admin only): pastoral and admin notes about the member. Never shown to the member
 * (there is no /me route for notes). api/members/{id}/notes, max 512 characters.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlus, IconEdit, IconTrash, IconAlertCircle, IconNotes, IconLock } from '@tabler/icons-react';
import { Card, Stack, Group, Button, Textarea, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, EmptyState, DatePickerInput } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import { useMemberService, type MemberNote } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { UserName } from '../UserName';
import { errorMessage, formatStampTime, dobToDate, dateToDob, formatDob } from '../memberFormat';

const MAX = 512;

export function NotesTab({ api, member, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const memberService = useMemberService();
  const notes = useQuery({ queryKey: api.keys.notes, queryFn: () => memberService.getNotes(member.memberID), enabled: perms.notes });
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [text, setText] = useState('');
  const [noteDate, setNoteDate] = useState<Date | null>(new Date());

  const refresh = () => queryClient.invalidateQueries({ queryKey: api.keys.notes });

  const save = useMutation({
    mutationFn: () => {
      const body = { note: text.trim(), noteDate: dateToDob(noteDate) };
      return editing?.id ? memberService.updateNote(member.memberID, editing.id, body) : memberService.addNote(member.memberID, body);
    },
    onSuccess: () => { toast.success('Saved', editing?.id ? 'Note updated.' : 'Note added.'); setEditing(null); refresh(); },
    onError: (e) => toast.error('Could not save the note', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (noteId: number) => memberService.deleteNote(member.memberID, noteId),
    onSuccess: () => { toast.success('Removed', 'Note deleted.'); refresh(); },
    onError: (e) => toast.error('Could not delete the note', errorMessage(e)),
  });

  const openAdd = () => { setText(''); setNoteDate(new Date()); save.reset(); setEditing({ id: null }); };
  const openEdit = (n: MemberNote) => { setText(n.note); setNoteDate(dobToDate(n.noteDate) ?? new Date()); save.reset(); setEditing({ id: n.noteID }); };

  const list = [...(notes.data ?? [])].sort((a, b) =>
    (b.noteDate ?? '').localeCompare(a.noteDate ?? '') || Number(b.createdDateTime ?? 0) - Number(a.createdDateTime ?? 0));

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap={6}><Text fw={600}>Notes</Text><IconLock size={14} style={{ opacity: 0.6 }} /></Group>
            <Text size="sm" c="dimmed">Pastoral and admin notes. Only staff with member access can see them; the member never does.</Text>
          </div>
          {perms.notesWrite && <Button size="sm" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add note</Button>}
        </Group>

        {notes.isLoading && <Stack gap="sm"><Skeleton height={80} /><Skeleton height={80} /></Stack>}
        {notes.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load notes">{errorMessage(notes.error)}</Alert>}
        {!notes.isLoading && !notes.isError && list.length === 0 && (
          <EmptyState size="sm" icon={<IconNotes size={32} />} title="No notes yet"
            actionLabel={perms.notesWrite ? 'Add note' : undefined} onAction={perms.notesWrite ? openAdd : undefined} />
        )}

        <Stack gap="sm">
          {list.map((n) => (
            <Card key={n.noteID} withBorder padding="md" radius="md">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.note}</Text>
                  <Text size="xs" c="dimmed">
                    {n.noteDate ? formatDob(n.noteDate) : formatStampTime(n.createdDateTime)}
                    {' · by '}<UserName userId={n.createdBy} fallback="a staff member" size="xs" />
                    {n.modifiedBy && n.modifiedBy !== n.createdBy && (<>{' · edited by '}<UserName userId={n.modifiedBy} fallback="a staff member" size="xs" /></>)}
                  </Text>
                </Stack>
                {perms.notesWrite && (
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Edit"><ActionIcon variant="subtle" aria-label="Edit note" onClick={() => openEdit(n)}><IconEdit size={16} /></ActionIcon></Tooltip>
                    <Tooltip label="Delete">
                      <ActionIcon variant="subtle" color="red" aria-label="Delete note"
                        onClick={() => modal.confirm({ title: 'Delete this note?', body: 'This cannot be undone.', confirmLabel: 'Delete', confirmColor: 'red', onConfirm: () => remove.mutate(n.noteID) })}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>

      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit note' : 'Add note'} centered size="lg">
        <Stack gap="md">
          <Textarea label="Note" required autosize minRows={4} maxLength={MAX} value={text} onChange={(e) => setText(e.currentTarget.value)}
            description={`${text.length} of ${MAX} characters`} />
          <DatePickerInput label="Date" value={noteDate} onChange={(v) => setNoteDate((v as Date | null) ?? null)} maxDate={new Date()} valueFormat="D MMMM YYYY" />
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={save.isPending} disabled={!text.trim()} onClick={() => save.mutate()}>Save note</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
